import React, { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import { FaEdit, FaTrash, FaBan, FaCheck, FaEye, FaUserShield, FaUserEdit, FaUserTimes } from 'react-icons/fa';

export default function UsersAdmin() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showBanned, setShowBanned] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [bulkAction, setBulkAction] = useState('');
  const [stats, setStats] = useState({
    total: 0,
    admins: 0,
    editors: 0,
    reviewers: 0,
    users: 0,
    banned: 0
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  async function fetchUsers() {
    setLoading(true);
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (!error) {
      setUsers(data || []);
      calculateStats(data || []);
    } else {
      console.error('Error fetching users:', error);
    }
    setLoading(false);
  }

  function calculateStats(userData) {
    const stats = {
      total: userData.length,
      admins: userData.filter(u => u.role === 'admin').length,
      editors: userData.filter(u => u.role === 'editor').length,
      reviewers: userData.filter(u => u.role === 'reviewer').length,
      users: userData.filter(u => u.role === 'user').length,
      banned: userData.filter(u => u.is_banned).length
    };
    setStats(stats);
  }

  function openEditModal(user) {
    setEditingUser(user);
    setEditForm({
      full_name: user.full_name || '',
      username: user.username || '',
      email: user.email || '',
      role: user.role || 'user',
      profession: user.profession || '',
      country: user.country || '',
      institution: user.institution || '',
      profile_visibility: user.profile_visibility || 'public',
      is_banned: user.is_banned || false,
      ban_reason: user.ban_reason || ''
    });
  }

  function closeEditModal() {
    setEditingUser(null);
    setEditForm({});
  }

  function handleEditChange(e) {
    setEditForm({ ...editForm, [e.target.name]: e.target.value });
  }

  async function handleEditSave() {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('users')
        .update(editForm)
        .eq('id', editingUser.id);
      
      if (!error) {
        await fetchUsers();
        closeEditModal();
        alert('User updated successfully!');
      } else {
        alert('Error updating user: ' + error.message);
      }
    } catch (err) {
      alert('Error updating user: ' + err.message);
    }
    setSaving(false);
  }

  async function handleDeleteUser(userId) {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }
    
    setDeleting(userId);
    try {
      const { error } = await supabase
        .from('users')
        .delete()
        .eq('id', userId);
      
      if (!error) {
        await fetchUsers();
        alert('User deleted successfully!');
      } else {
        alert('Error deleting user: ' + error.message);
      }
    } catch (err) {
      alert('Error deleting user: ' + err.message);
    }
    setDeleting(null);
  }

  async function handleToggleBan(user) {
    const newBanStatus = !user.is_banned;
    const banReason = newBanStatus ? window.prompt('Enter reason for banning this user:') : '';
    
    if (newBanStatus && !banReason) {
      alert('Please provide a reason for banning the user.');
      return;
    }

    try {
      const { error } = await supabase
        .from('users')
        .update({ 
          is_banned: newBanStatus,
          ban_reason: newBanStatus ? banReason : null
        })
        .eq('id', user.id);
      
      if (!error) {
        await fetchUsers();
        alert(`User ${newBanStatus ? 'banned' : 'unbanned'} successfully!`);
      } else {
        alert('Error updating user status: ' + error.message);
      }
    } catch (err) {
      alert('Error updating user status: ' + err.message);
    }
  }

  async function handleBulkAction() {
    if (!bulkAction || selectedUsers.length === 0) {
      alert('Please select an action and at least one user.');
      return;
    }

    const confirmMessage = `Are you sure you want to ${bulkAction} ${selectedUsers.length} user(s)?`;
    if (!window.confirm(confirmMessage)) return;

    setSaving(true);
    try {
      let updateData = {};
      
      switch (bulkAction) {
        case 'ban':
          const banReason = window.prompt('Enter reason for banning these users:');
          if (!banReason) {
            alert('Please provide a reason for banning.');
            setSaving(false);
            return;
          }
          updateData = { is_banned: true, ban_reason: banReason };
          break;
        case 'unban':
          updateData = { is_banned: false, ban_reason: null };
          break;
        case 'delete':
          // Handle bulk delete
          for (const userId of selectedUsers) {
            await supabase.from('users').delete().eq('id', userId);
          }
          await fetchUsers();
          setSelectedUsers([]);
          setBulkAction('');
          alert('Users deleted successfully!');
          setSaving(false);
          return;
        default:
          alert('Invalid action selected.');
          setSaving(false);
          return;
      }

      // Update selected users
      for (const userId of selectedUsers) {
        await supabase.from('users').update(updateData).eq('id', userId);
      }
      
      await fetchUsers();
      setSelectedUsers([]);
      setBulkAction('');
      alert(`Users ${bulkAction}ed successfully!`);
    } catch (err) {
      alert('Error performing bulk action: ' + err.message);
    }
    setSaving(false);
  }

  function handleSelectUser(userId) {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  }

  function handleSelectAll() {
    if (selectedUsers.length === filteredUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map(u => u.id));
    }
  }

  // Filter users based on search term, role, and status
  const filteredUsers = users.filter(user => {
    const matchesSearch = !searchTerm || 
      user.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRole = !roleFilter || user.role === roleFilter;
    const matchesStatus = !statusFilter || 
      (statusFilter === 'banned' && user.is_banned) ||
      (statusFilter === 'active' && !user.is_banned);
    
    return matchesSearch && matchesRole && matchesStatus;
  });

  const getRoleBadge = (role) => {
    const badges = {
      admin: 'danger',
      editor: 'success', 
      reviewer: 'info',
      user: 'secondary'
    };
    return <span className={`badge bg-${badges[role] || 'secondary'}`}>{role}</span>;
  };

  const getStatusBadge = (user) => {
    if (user.is_banned) {
      return <span className="badge bg-danger">Banned</span>;
    }
    return <span className="badge bg-success">Active</span>;
  };

  return (
    <div className="card shadow-lg border-0 rounded-4">
      <div className="card-body p-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h3 className="mb-0">
            <FaUserShield className="me-2 text-primary" />
            Users Management
          </h3>
          <div className="d-flex gap-2">
            <button 
              className={`btn btn-sm ${showBanned ? 'btn-danger' : 'btn-outline-danger'}`}
              onClick={() => setShowBanned(!showBanned)}
            >
              {showBanned ? 'Hide Banned' : 'Show Banned'}
            </button>
          </div>
        </div>

        {/* Statistics */}
        <div className="row mb-4">
          <div className="col-md-2">
            <div className="card bg-primary text-white">
              <div className="card-body text-center">
                <h4>{stats.total}</h4>
                <small>Total Users</small>
              </div>
            </div>
          </div>
          <div className="col-md-2">
            <div className="card bg-danger text-white">
              <div className="card-body text-center">
                <h4>{stats.admins}</h4>
                <small>Admins</small>
              </div>
            </div>
          </div>
          <div className="col-md-2">
            <div className="card bg-success text-white">
              <div className="card-body text-center">
                <h4>{stats.editors}</h4>
                <small>Editors</small>
              </div>
            </div>
          </div>
          <div className="col-md-2">
            <div className="card bg-info text-white">
              <div className="card-body text-center">
                <h4>{stats.reviewers}</h4>
                <small>Reviewers</small>
              </div>
            </div>
          </div>
          <div className="col-md-2">
            <div className="card bg-secondary text-white">
              <div className="card-body text-center">
                <h4>{stats.users}</h4>
                <small>Users</small>
              </div>
            </div>
          </div>
          <div className="col-md-2">
            <div className="card bg-warning text-dark">
              <div className="card-body text-center">
                <h4>{stats.banned}</h4>
                <small>Banned</small>
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Bulk Actions */}
        <div className="row mb-4">
          <div className="col-md-3">
            <input
              type="text"
              className="form-control"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="col-md-2">
            <select
              className="form-select"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              <option value="">All Roles</option>
              <option value="admin">Admin</option>
              <option value="editor">Editor</option>
              <option value="reviewer">Reviewer</option>
              <option value="user">User</option>
            </select>
          </div>
          <div className="col-md-2">
            <select
              className="form-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="banned">Banned</option>
            </select>
          </div>
          <div className="col-md-2">
            <select
              className="form-select"
              value={bulkAction}
              onChange={(e) => setBulkAction(e.target.value)}
            >
              <option value="">Bulk Actions</option>
              <option value="ban">Ban Selected</option>
              <option value="unban">Unban Selected</option>
              <option value="delete">Delete Selected</option>
            </select>
          </div>
          <div className="col-md-3">
            <div className="d-flex gap-2">
              <button 
                className="btn btn-primary" 
                onClick={handleBulkAction}
                disabled={!bulkAction || selectedUsers.length === 0 || saving}
              >
                {saving ? 'Processing...' : 'Apply'}
              </button>
              <button className="btn btn-secondary" onClick={fetchUsers}>
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Users Table */}
        {loading ? (
          <div className="text-center my-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="alert alert-info">No users found.</div>
        ) : (
          <div className="table-responsive">
            <table className="table table-bordered align-middle">
              <thead className="table-light">
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th>Avatar</th>
                  <th>Name</th>
                  <th>Username</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Joined</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map(user => (
                  <tr key={user.id} className={user.is_banned ? 'table-danger' : ''}>
                    <td>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={selectedUsers.includes(user.id)}
                        onChange={() => handleSelectUser(user.id)}
                      />
                    </td>
                    <td>
                      {user.profile_image_url ? (
                        <img 
                          src={user.profile_image_url} 
                          alt="avatar" 
                          style={{width: 40, height: 40, borderRadius: '50%', objectFit: 'cover'}}
                        />
                      ) : (
                        <div className="bg-secondary rounded-circle d-flex align-items-center justify-content-center" 
                             style={{width: 40, height: 40}}>
                          <span className="text-white">{user.full_name?.charAt(0) || user.username?.charAt(0) || 'U'}</span>
                        </div>
                      )}
                    </td>
                    <td>
                      <div>
                        <strong>{user.full_name || 'N/A'}</strong>
                        {user.profession && <div className="small text-muted">{user.profession}</div>}
                      </div>
                    </td>
                    <td>{user.username || 'N/A'}</td>
                    <td>{user.email}</td>
                    <td>{getRoleBadge(user.role)}</td>
                    <td>{getStatusBadge(user)}</td>
                    <td>{new Date(user.created_at).toLocaleDateString()}</td>
                    <td>
                      <div className="d-flex gap-1">
                        <button 
                          className="btn btn-outline-primary btn-sm" 
                          onClick={() => openEditModal(user)}
                          title="Edit User"
                        >
                          <FaEdit />
                        </button>
                        <button 
                          className={`btn btn-sm ${user.is_banned ? 'btn-success' : 'btn-warning'}`}
                          onClick={() => handleToggleBan(user)}
                          title={user.is_banned ? 'Unban User' : 'Ban User'}
                        >
                          {user.is_banned ? <FaCheck /> : <FaBan />}
                        </button>
                        <button 
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => handleDeleteUser(user.id)}
                          disabled={deleting === user.id}
                          title="Delete User"
                        >
                          {deleting === user.id ? '...' : <FaTrash />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Selected Users Info */}
        {selectedUsers.length > 0 && (
          <div className="alert alert-info mt-3">
            <strong>{selectedUsers.length}</strong> user(s) selected for bulk action.
          </div>
        )}

        {/* Edit User Modal */}
        {editingUser && (
          <div className="modal fade show" style={{display:'block'}} tabIndex="-1" role="dialog">
            <div className="modal-dialog modal-lg" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <FaUserEdit className="me-2" />
                    Edit User: {editingUser.full_name || editingUser.username || editingUser.email}
                  </h5>
                  <button type="button" className="btn-close" onClick={closeEditModal}></button>
                </div>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Full Name</label>
                        <input 
                          type="text" 
                          className="form-control" 
                          name="full_name" 
                          value={editForm.full_name} 
                          onChange={handleEditChange} 
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Username</label>
                        <input 
                          type="text" 
                          className="form-control" 
                          name="username" 
                          value={editForm.username} 
                          onChange={handleEditChange} 
                        />
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Email</label>
                        <input 
                          type="email" 
                          className="form-control" 
                          name="email" 
                          value={editForm.email} 
                          onChange={handleEditChange} 
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Role</label>
                        <select 
                          className="form-select" 
                          name="role" 
                          value={editForm.role} 
                          onChange={handleEditChange}
                        >
                          <option value="user">User</option>
                          <option value="reviewer">Reviewer</option>
                          <option value="editor">Editor</option>
                          <option value="admin">Admin</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Profession</label>
                        <input 
                          type="text" 
                          className="form-control" 
                          name="profession" 
                          value={editForm.profession} 
                          onChange={handleEditChange} 
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Country</label>
                        <input 
                          type="text" 
                          className="form-control" 
                          name="country" 
                          value={editForm.country} 
                          onChange={handleEditChange} 
                        />
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Institution</label>
                        <input 
                          type="text" 
                          className="form-control" 
                          name="institution" 
                          value={editForm.institution} 
                          onChange={handleEditChange} 
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Profile Visibility</label>
                        <select 
                          className="form-select" 
                          name="profile_visibility" 
                          value={editForm.profile_visibility} 
                          onChange={handleEditChange}
                        >
                          <option value="public">Public</option>
                          <option value="private">Private</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">
                          <input 
                            type="checkbox" 
                            className="form-check-input me-2" 
                            name="is_banned" 
                            checked={editForm.is_banned} 
                            onChange={(e) => setEditForm({...editForm, is_banned: e.target.checked})} 
                          />
                          Banned
                        </label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Ban Reason</label>
                        <input 
                          type="text" 
                          className="form-control" 
                          name="ban_reason" 
                          value={editForm.ban_reason} 
                          onChange={handleEditChange}
                          disabled={!editForm.is_banned}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closeEditModal}>
                    Cancel
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-primary" 
                    onClick={handleEditSave} 
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>
            </div>
            <div className="modal-backdrop fade show"></div>
          </div>
        )}
      </div>
    </div>
  );
} 